import map from 'lodash/map'
import trim from 'lodash/trim'
import { PivotFieldTypeEnum, PivotFilterOperatorEnum, PivotRelativeUnitEnum } from '@ztechapps/common.contract'
import {
  PIVOT_OPERATOR_LABELS,
  PIVOT_VALUE_COMPARISON_OPERATORS,
  isPivotNumericOperand,
  isPivotRangeOperator,
  isPivotRelativeOperator,
} from '@ztechapps/common.utils'
import { applyConditionFilter } from './applyConditionFilter'

const O = PivotFilterOperatorEnum

const STRING_OPERATORS = [
  O.equal,
  O.not_equal,
  O.begin,
  O.not_begin,
  O.end,
  O.not_end,
  O.contain,
  O.not_contain,
  O.greater,
  O.greater_equal,
  O.less,
  O.less_equal,
  O.between,
  O.not_between,
]
const DATE_OPERATORS = [
  O.equal,
  O.not_equal,
  O.before,
  O.before_equal,
  O.after,
  O.after_equal,
  O.between,
  O.not_between,
  O.last,
  O.current,
  O.next,
]

const toOptions = (operators: PivotFilterOperatorEnum[]) =>
  map(operators, (value) => ({ label: PIVOT_OPERATOR_LABELS[value], value }))

/** The operator options of a Condition per field type — a date offers the relative operators after its eight. */
export const PIVOT_CONDITION_OPERATOR_OPTIONS: Record<
  PivotFieldTypeEnum,
  { label: string; value: PivotFilterOperatorEnum }[]
> = {
  [PivotFieldTypeEnum.Number]: toOptions(PIVOT_VALUE_COMPARISON_OPERATORS),
  [PivotFieldTypeEnum.String]: toOptions(STRING_OPERATORS),
  [PivotFieldTypeEnum.Date]: toOptions(DATE_OPERATORS),
}

/** The operator options of a Value filter — Top and Bottom N, then the eight comparisons. */
export const PIVOT_VALUE_OPERATOR_OPTIONS = toOptions([O.top, O.bottom, ...PIVOT_VALUE_COMPARISON_OPERATORS])

/** The unit options of a relative date operator. */
export const PIVOT_RELATIVE_UNIT_OPTIONS = map(PivotRelativeUnitEnum, (value) => ({
  label: `${value.charAt(0).toUpperCase()}${value.slice(1)}`,
  value,
}))

/** The operands an operator keeps of the two inputs. */
export const toPivotOperandValues = (operator: PivotFilterOperatorEnum, value1: string, value2: string): string[] => {
  if (isPivotRelativeOperator(operator)) return []
  return isPivotRangeOperator(operator) ? [value1, value2] : [value1]
}

/** Problems with the two operand inputs of an operator, by input. */
export type PivotOperandProblems = { value1?: string; value2?: string }

// whether From is greater than To, compared as the filter compares members — numbers by value, text ignoring case, days as ISO text
const isFromAfterTo = (valueType: PivotFieldTypeEnum, from: string, to: string): boolean =>
  applyConditionFilter({
    condition: { operator: O.greater, values: [to] },
    memberKey: from,
    fieldType: valueType,
    today: '',
  })

/** Finds what keeps an operator's operands from being applied — empty, not a number, or From greater than To. */
export const findPivotOperandProblems = ({
  operator,
  value1,
  value2,
  valueType,
}: {
  operator: PivotFilterOperatorEnum
  value1: string
  value2: string
  valueType: PivotFieldTypeEnum
}): PivotOperandProblems => {
  if (isPivotRelativeOperator(operator)) return {}
  const problemOf = (value: string): string | undefined => {
    if (trim(value) === '') return valueType === PivotFieldTypeEnum.Date ? 'Pick a date' : 'Enter a value'
    if (valueType === PivotFieldTypeEnum.Number && !isPivotNumericOperand(value))
      return 'Enter a number, for example 12.5'
    return undefined
  }
  const problems: PivotOperandProblems = { value1: problemOf(value1) }
  if (isPivotRangeOperator(operator)) {
    problems.value2 = problemOf(value2)
    if (!problems.value1 && !problems.value2 && isFromAfterTo(valueType, trim(value1), trim(value2)))
      problems.value2 =
        valueType === PivotFieldTypeEnum.Number ? 'To must not be less than From' : 'To must not be before From'
  }
  return problems
}
